import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getRoleLabelText} from 'samples/experimental-roles/ExperimentalRoleUtil';
import {ConcentrationLayerContext} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {
  MAX_METADATA_LAYER_NAME_LENGTH,
  PlateMap,
  PlateMapWell,
  ReagentType,
  Source,
} from 'samples/plate-maps/PlateMapConstants';
import {formatGridCoordinates} from 'samples/SampleUtil';

import {
  ConcentrationLayerInput,
  ContentItemInput,
  MetadataColumnInput,
  MetadataLayerApiType,
  ParameterLayerInput,
  ScratchPlateWellInput,
} from '../../../plates/dao/ScratchPlateDAO';
import {serializeSchemaTag, ScratchPlateDesignState} from './ScratchPlateDesignModalUtil';

// -- Step definitions ---------------------------------------------------------

export enum CreateScratchPlateRecordsModalStep {
  PLATE_DETAILS = 'PLATE_DETAILS',
  UPDATE_PLATE_DESIGN = 'UPDATE_PLATE_DESIGN',
  DEFINE_TRANSFER_CONTENTS = 'DEFINE_TRANSFER_CONTENTS',
  REVIEW = 'REVIEW',
}

export function getCreateScratchPlateRecordsModalSteps(): Array<{
  key: CreateScratchPlateRecordsModalStep;
  title: string;
}> {
  return [
    {key: CreateScratchPlateRecordsModalStep.PLATE_DETAILS, title: 'Plate details'},
    {key: CreateScratchPlateRecordsModalStep.UPDATE_PLATE_DESIGN, title: 'Update plate design'},
    {key: CreateScratchPlateRecordsModalStep.DEFINE_TRANSFER_CONTENTS, title: 'Define contents'},
    {key: CreateScratchPlateRecordsModalStep.REVIEW, title: 'Review'},
  ];
}

// -- Shared types and subgroup helpers ----------------------------------------

export const UNLABELED_PRIMARY_ROLE = '__unlabeled__' as const;

export interface SubgroupKey {
  primaryRole: PrimaryExperimentalRoleType | typeof UNLABELED_PRIMARY_ROLE;
  subrole?: string;
  group: number;
}

export const UNLABELED_SUBGROUP_KEY: SubgroupKey = {
  primaryRole: UNLABELED_PRIMARY_ROLE,
  subrole: undefined,
  group: 0,
};

export const PRIMARY_ROLE_ORDER = [
  PrimaryExperimentalRoleType.SAMPLE,
  PrimaryExperimentalRoleType.CONTROL,
  PrimaryExperimentalRoleType.STANDARD,
  PrimaryExperimentalRoleType.BLANK,
  UNLABELED_PRIMARY_ROLE,
];

export const SUBROLE_ORDER = [undefined, 'MAXIMUM', 'MINIMUM', 'POSITIVE', 'NEGATIVE'];

export function subgroupKeyToString(key: SubgroupKey): string {
  return `${key.primaryRole}|${key.subrole ?? ''}|${key.group}`;
}

export function subgroupLabel(key: SubgroupKey): string {
  if (key.primaryRole === UNLABELED_PRIMARY_ROLE) {
    return 'Unlabeled';
  }
  return getRoleLabelText({
    primaryRole: key.primaryRole,
    subrole: key.subrole ?? undefined,
    group: key.group,
  });
}

export function sortSubgroups(subgroups: Array<SubgroupKey>): Array<SubgroupKey> {
  // Group the subgroups by primary role
  const grouped = new Map<SubgroupKey['primaryRole'], Array<SubgroupKey>>();
  for (const sg of subgroups) {
    const existing = grouped.get(sg.primaryRole);
    if (existing != null) {
      existing.push(sg);
    } else {
      grouped.set(sg.primaryRole, [sg]);
    }
  }

  // Primary sort by primary role order: sample, control, standard, blank
  // Secondary sort by subrole, if it exists
  // Tertiary sort by group
  const orderedSubgroups: Array<SubgroupKey> = [];
  for (const primaryRole of PRIMARY_ROLE_ORDER) {
    if (!grouped.has(primaryRole)) {
      continue;
    }
    for (const subrole of SUBROLE_ORDER) {
      orderedSubgroups.push(
        ...grouped
          .get(primaryRole)!
          .filter((sg) => sg.subrole === subrole)
          .sort((a, b) => a.group - b.group)
      );
    }
  }
  return orderedSubgroups;
}

export function getSortedSubgroups(plateMap: PlateMap): Array<SubgroupKey> {
  const seen = new Set<string>();
  const result: Array<SubgroupKey> = [];
  for (const well of plateMap.wells) {
    if (well.role == null) {
      continue;
    }
    const key: SubgroupKey = {
      primaryRole: well.role.primaryRole,
      subrole: well.role.subrole ?? undefined,
      group: well.role.group,
    };
    const str = subgroupKeyToString(key);
    if (!seen.has(str)) {
      seen.add(str);
      result.push(key);
    }
  }
  if (plateMap.wells.some((w) => w.role == null)) {
    result.push(UNLABELED_SUBGROUP_KEY);
  }
  return sortSubgroups(result);
}

export function countDistinctSourcesForSubgroup(
  plateMap: PlateMap,
  subgroup: SubgroupKey,
  resolvedPlates: ResolvedPlates
): number {
  const wells = getWellsForSubgroup(plateMap, subgroup);
  const wellIndices = new Set(
    wells.map((w) => w.position.row * plateMap.size.numColumns + w.position.column)
  );
  const sourceIds = new Set<string>();
  for (const plateEntry of resolvedPlates) {
    for (const [wellIndexStr, sources] of Object.entries(plateEntry)) {
      if (wellIndices.has(Number(wellIndexStr))) {
        for (const source of sources) {
          sourceIds.add(source.id);
        }
      }
    }
  }
  return sourceIds.size;
}

export function getWellsForSubgroup(plateMap: PlateMap, subgroup: SubgroupKey): Array<PlateMapWell> {
  if (subgroup.primaryRole === UNLABELED_PRIMARY_ROLE) {
    return plateMap.wells.filter((w) => w.role == null);
  }
  return plateMap.wells.filter(
    (w) =>
      w.role?.primaryRole === subgroup.primaryRole &&
      (w.role?.subrole ?? undefined) === subgroup.subrole &&
      w.role?.group === subgroup.group
  );
}

export function isBlankWell(well: PlateMapWell): boolean {
  return well.role?.primaryRole === PrimaryExperimentalRoleType.BLANK;
}

/** Per-plate resolved well assignment: index in array = plate index, value = map of wellIndex → sources. */
export type ResolvedPlates = Array<{[wellIndex: number]: Array<Source>}>;

/**
 * Merges per-layer ResolvedPlates into a single flat ResolvedPlates by concatenating each layer's
 * sources for the same plate index and well index.
 */
export function mergeLayerResolvedPlates(
  layerStates: Array<{resolvedPlates: ResolvedPlates}>
): ResolvedPlates {
  const maxPlates = Math.max(0, ...layerStates.map((ls) => ls.resolvedPlates.length));
  const merged: ResolvedPlates = [];
  for (let i = 0; i < maxPlates; i++) {
    const plateEntry: {[wellIndex: number]: Array<Source>} = {};
    for (const {resolvedPlates} of layerStates) {
      const layerPlate = resolvedPlates[i];
      if (layerPlate == null) {
        continue;
      }
      for (const [wellIndexStr, sources] of Object.entries(layerPlate)) {
        const wellIndex = Number(wellIndexStr);
        plateEntry[wellIndex] = [...(plateEntry[wellIndex] ?? []), ...sources];
      }
    }
    merged.push(plateEntry);
  }
  return merged;
}

/**
 * Drops trailing entries with no source assignments. The Define Contents step can leave behind
 * empty plate entries when the user clears a previously-assigned plate; without trimming, they
 * would surface as extra blank plates in the Review preview and as empty inner `contents` arrays
 * (= extra empty scratch plates) in the create payload.
 */
export function trimTrailingEmptyResolvedPlates(resolvedPlates: ResolvedPlates): ResolvedPlates {
  let lastNonEmpty = -1;
  for (let i = 0; i < resolvedPlates.length; i++) {
    if (Object.keys(resolvedPlates[i]).length > 0) {
      lastNonEmpty = i;
    }
  }
  return lastNonEmpty === resolvedPlates.length - 1
    ? resolvedPlates
    : resolvedPlates.slice(0, lastNonEmpty + 1);
}

// -- API payload builders -----------------------------------------------------

export function buildWells(
  plateMap: NonNullable<ScratchPlateDesignState['plateMap']>
): Array<ScratchPlateWellInput> {
  return plateMap.wells.map((well) => {
    const tagEntries = Object.entries(well.schemaTags);
    const schemaTags =
      tagEntries.length > 0
        ? Object.fromEntries(tagEntries.map(([apiId, tag]) => [apiId, serializeSchemaTag(tag)]))
        : undefined;
    const volumeMeasurement = well.quantity;
    return {
      rowIndex: well.position.row,
      columnIndex: well.position.column,
      role: well.role?.primaryRole ?? null,
      subrole: well.role?.subrole ?? undefined,
      group: well.role?.group != null ? String(well.role.group) : undefined,
      sampleSchemaId: undefined,
      schemaTags,
      wellVolumeAmount: volumeMeasurement.value ?? undefined,
      wellVolumeUnitId: volumeMeasurement.unit?.id ?? undefined,
      wellVolumeUnitVersionNumber: volumeMeasurement.unit?.versionNumber ?? undefined,
    };
  });
}

export function buildConcentrationLayers(
  plateMap: NonNullable<ScratchPlateDesignState['plateMap']>,
  concentrationLayerContext: ConcentrationLayerContext
): Array<ConcentrationLayerInput> {
  if (!plateMap.concentrationLayerInfoById) {
    return [];
  }
  return Object.entries(plateMap.concentrationLayerInfoById).map(([layerId, info]) => {
    const layerData = concentrationLayerContext.layers.get(layerId);
    const machineUnit = layerData?.concentrationUnit ?? undefined;
    const wellUnit =
      machineUnit == null
        ? (plateMap.wells.find((w) => w.concentrationLayers[layerId]?.unit != null)?.concentrationLayers[
            layerId
          ]?.unit ?? undefined)
        : undefined;
    const unitApiId = machineUnit?.id ?? wellUnit?.id ?? '';
    const unitVersionNumber = machineUnit?.versionNumber ?? wellUnit?.versionNumber ?? 0;
    return {
      name: info.name,
      concentrationUnitId: unitApiId,
      concentrationUnitVersionNumber: unitVersionNumber,
      wells: plateMap.wells.map((well) => ({
        wellPosition: formatGridCoordinates(well.position.row, well.position.column),
        concentrationAmount: well.concentrationLayers[layerId]?.value ?? undefined,
      })),
    };
  });
}

const LEAF_TYPE_TO_API_TYPE: Partial<Record<LeafType, MetadataLayerApiType>> = {
  [LeafType.TEXT]: 'text',
  [LeafType.DECIMAL]: 'float',
  [LeafType.INTEGER]: 'integer',
  [LeafType.BOOLEAN]: 'boolean',
  [LeafType.DATE]: 'date',
  [LeafType.DATETIME]: 'datetime',
};

export function buildMetadataLayers(
  plateMap: NonNullable<ScratchPlateDesignState['plateMap']>
): Array<MetadataColumnInput> {
  if (!plateMap.metadataLayerInfoById) {
    return [];
  }
  return Object.entries(plateMap.metadataLayerInfoById).map(([layerId, info]) => ({
    name: info.name,
    type: LEAF_TYPE_TO_API_TYPE[info.type] ?? 'text',
    wells: plateMap.wells
      .filter((well) => well.metadataLayers?.[layerId] != null)
      .map((well) => ({
        wellPosition: formatGridCoordinates(well.position.row, well.position.column),
        value: well.metadataLayers![layerId],
      })),
  }));
}

export function buildParameterLayers(
  plateMap: NonNullable<ScratchPlateDesignState['plateMap']>
): Array<ParameterLayerInput> {
  if (!plateMap.parameterLayerInfoById) {
    return [];
  }
  return Object.entries(plateMap.parameterLayerInfoById).map(([layerId, info]) => ({
    parameterVersionId: layerId,
    name: info.name,
    fieldType: info.fieldType,
    wells: plateMap.wells
      .filter((well) => well.parameterLayers?.[layerId] != null)
      .map((well) => ({
        wellPosition: formatGridCoordinates(well.position.row, well.position.column),
        value: well.parameterLayers![layerId],
      })),
  }));
}

export function getInvalidConcentrationLayerTooltip(
  concentrationLayerContext: ConcentrationLayerContext,
  plateMap: ScratchPlateDesignState['plateMap']
): string | null {
  if (plateMap?.concentrationLayerInfoById == null) {
    return null;
  }
  for (const [layerId, info] of Object.entries(plateMap.concentrationLayerInfoById)) {
    const layerData = concentrationLayerContext.layers.get(layerId);
    const machineHasUnit = layerData?.concentrationUnit != null;
    const wellHasUnit = plateMap.wells.some((w) => w.concentrationLayers[layerId]?.unit != null);
    if (!machineHasUnit && !wellHasUnit) {
      return `Concentration layer "${info.name}" is invalid: Either specify a unit or delete the layer`;
    }
  }
  return null;
}

export function getInvalidMetadataLayerTooltip(plateMap: ScratchPlateDesignState['plateMap']): string | null {
  if (plateMap?.metadataLayerInfoById == null) {
    return null;
  }
  const normalizedNames = new Set<string>();
  for (const info of Object.values(plateMap.metadataLayerInfoById)) {
    const normalizedName = info.name.trim();
    if (normalizedName.length === 0) {
      return 'Metadata layer name must not be empty';
    }
    if (normalizedName.length > MAX_METADATA_LAYER_NAME_LENGTH) {
      return `Metadata layer name must be ${MAX_METADATA_LAYER_NAME_LENGTH} characters or fewer`;
    }
    if (normalizedNames.has(normalizedName)) {
      return 'Metadata layer names must be unique';
    }
    normalizedNames.add(normalizedName);
  }
  return null;
}

export interface ContentsLayerForContents {
  name: string;
  resolvedPlates: ResolvedPlates;
}

export function buildContents(
  plateMap: NonNullable<PlateMap>,
  contentsLayers: Array<ContentsLayerForContents>,
  trimmedMergedResolvedPlates: ResolvedPlates
): Array<Array<ContentItemInput>> {
  const numColumns = plateMap.size.numColumns;

  return trimmedMergedResolvedPlates.map((_, plateIndex) => {
    const plateContents: Array<ContentItemInput> = [];
    for (const layer of contentsLayers) {
      const wellSourcesById = layer.resolvedPlates[plateIndex];
      if (wellSourcesById == null) {
        continue;
      }
      for (const [wellIndexStr, sources] of Object.entries(wellSourcesById)) {
        const wellIndex = Number(wellIndexStr);
        const row = Math.floor(wellIndex / numColumns);
        const column = wellIndex % numColumns;
        for (const source of sources) {
          const concentration =
            source.type === ReagentType.SOURCE_CONTAINABLE
              ? (source.finalConcentration?.measurement ?? undefined)
              : undefined;
          const transferQuantity =
            source.type === ReagentType.SOURCE_CONTAINABLE
              ? (source.transferQuantity?.measurement ?? undefined)
              : undefined;
          // A container source carries a container id; send it as containerApiId so the backend
          // resolves it to the entity the container holds. Sending it as entityApiId would store a
          // container id on the content layer and crash the record view.
          const identifier =
            source.type === ReagentType.SOURCE_CONTAINER ? {containerId: source.id} : {entityId: source.id};
          plateContents.push({
            ...identifier,
            wellPosition: formatGridCoordinates(row, column),
            rowIndex: row,
            columnIndex: column,
            contentLayerName: layer.name,
            concentrationAmount: concentration?.value ?? undefined,
            concentrationUnitId: concentration?.unit?.id ?? undefined,
            concentrationUnitVersionNumber: concentration?.unit?.versionNumber ?? undefined,
            transferQuantityAmount: transferQuantity?.value ?? undefined,
            transferQuantityUnitId: transferQuantity?.unit?.id ?? undefined,
            transferQuantityUnitVersionNumber: transferQuantity?.unit?.versionNumber ?? undefined,
          });
        }
      }
    }
    return plateContents;
  });
}
